/* Optional adapter for the observed Essential Preorder storefront markup.
 * This is not a vendor-supported API. See docs/ESSENTIAL.md before enabling.
 * Reads Essential's state; never changes its form, campaign, cart or checkout.
 */
(function (global) {
  'use strict';

  const BUTTON = '[data-essential-preorder-element="button"]';
  const CAMPAIGN = 'properties[_essential_preorder_campaign_id]';
  const cleanId = (value) => /^[1-9]\d*$/.test(String(value ?? '')) ? String(value) : null;

  function controls(form) {
    return form?.elements ? Array.from(form.elements) : Array.from(form?.querySelectorAll('input,select,button') || []);
  }

  function visible(element) {
    if (!element || element.hidden || element.closest('[hidden], [aria-hidden="true"]')) return false;
    if (!element.getClientRects().length) return false;
    const style = element.ownerDocument.defaultView.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function readSignal(form, isVisible = visible) {
    if (!form) return null;
    const fields = controls(form);
    const campaign = fields.find((field) => field.name === CAMPAIGN && !field.disabled);
    const variant = fields.find((field) => field.name === 'id');
    const product = fields.find((field) => field.name === 'product-id');
    const candidates = Array.from(form.ownerDocument.querySelectorAll(BUTTON)).filter((button) => {
      return button.form === form || form.contains(button) || (form.id && button.getAttribute('form') === form.id);
    });
    const button = candidates.find((candidate) => isVisible(candidate));
    if (!button || !campaign || !String(campaign.value || '').trim()) return null;
    const capacity = button.getAttribute('data-essential-preorder-inventory-available');
    const hasCapacity = capacity === null || (Number.isFinite(Number(capacity)) && Number(capacity) > 0);
    return {
      button,
      campaign,
      variantId: cleanId(variant?.value),
      productId: cleanId(product?.value),
      requiresContact: fields.some((field) => field.name === 'selling_plan' && !field.disabled && String(field.value || '').trim()),
      eligible: !button.disabled && button.getAttribute('aria-disabled') !== 'true' && hasCapacity,
      signature: JSON.stringify([String(campaign.value), button.textContent, button.getAttribute('aria-label'), capacity])
    };
  }

  // A changed form variant ID alone is not proof Essential has recalculated.
  // Identical retained app markers are deliberately held back after a variant
  // change until Essential removes/recreates/changes them, or an explicit event.
  function signalChanged(previous, current) {
    return !previous || !current || previous.button !== current.button ||
      previous.campaign !== current.campaign || previous.signature !== current.signature;
  }

  class EssentialAdapter {
    constructor(host, options = {}) {
      this.host = host;
      this.document = host.ownerDocument;
      this.isVisible = options.isVisible || visible;
      this.Observer = options.MutationObserver || this.document.defaultView.MutationObserver;
      this.variantId = cleanId(host.dataset.variantId);
      this.base = new WeakMap();
      this.summaryBase = new WeakMap();
      this.guard = null;
      this.override = null;
      this.destroyed = false;
      this.loading = (event) => this.onLoading(event);
      this.updated = () => this.sync();
      this.explicit = (event) => this.onExplicit(event);
      host.addEventListener('sh:cc:loading', this.loading);
      host.addEventListener('sh:cc:updated', this.updated);
      this.document.addEventListener('sh:preorder-change', this.explicit);
      this.sync();
    }

    findForm() {
      if (typeof this.host.getProductForm === 'function') return this.host.getProductForm();
      const explicit = this.host.dataset.formId && this.document.getElementById(this.host.dataset.formId);
      if (explicit?.tagName === 'FORM') return explicit;
      const scope = this.host.closest('product-page, product-info, .shopify-section') || this.host.parentElement;
      return Array.from(scope?.querySelectorAll('form[action*="/cart/add"]') || []).find((form) => {
        const fields = controls(form);
        const product = fields.find((field) => field.name === 'product-id');
        return fields.some((field) => field.name === 'id') &&
          (!product || String(product.value) === this.host.dataset.productId) &&
          !/installment/i.test(form.id);
      }) || null;
    }

    bindForm() {
      const form = this.findForm();
      if (this.form === form) return form;
      this.observer?.disconnect();
      this.form = form;
      if (form && this.Observer) {
        this.observer = new this.Observer(() => this.queueSync());
        // Observe the form's parent: Essential can place its button beside it.
        this.observer.observe(form.parentElement || form, {
          subtree: true, childList: true, characterData: true, attributes: true,
          attributeFilter: ['value', 'disabled', 'aria-disabled', 'aria-label', 'hidden', 'style', 'class', 'data-essential-preorder-inventory-available']
        });
      }
      return form;
    }

    queueSync() {
      if (this.queued || this.destroyed) return;
      this.queued = true;
      queueMicrotask(() => {
        this.queued = false;
        if (!this.destroyed) this.sync();
      });
    }

    onLoading(event) {
      const id = cleanId(event.detail?.variantId);
      const changed = id !== this.variantId;
      if (changed) {
        const previous = readSignal(this.findForm(), this.isVisible);
        this.guard = previous ? { previous, sawInactive: false } : null;
        this.override = null;
      }
      this.variantId = id;
      if (changed) this.restore();
    }

    onExplicit(event) {
      const detail = event.detail || {};
      // An integration must identify both resources; no global boolean state.
      if (cleanId(detail.productId) !== cleanId(this.host.dataset.productId) ||
          cleanId(detail.variantId) !== this.variantId || typeof detail.enabled !== 'boolean') return;
      this.override = {
        enabled: detail.enabled,
        message: typeof detail.message === 'string' ? detail.message : '',
        requiresContact: detail.requiresContact === true
      };
      this.sync();
    }

    remember(fragment) {
      fragment.querySelectorAll('.sh-cc-store').forEach((row) => {
        if (this.base.has(row)) return;
        const status = row.querySelector('.sh-cc-status');
        const note = row.querySelector('.sh-cc-collect');
        if (status && note) this.base.set(row, {
          statusText: status.textContent, status: status.dataset.status,
          noteText: note.textContent, noteStatus: note.dataset.status, noteHidden: note.hidden
        });
      });
      const summary = fragment.querySelector('[data-cc-summary]');
      if (summary && !this.summaryBase.has(summary)) {
        this.summaryBase.set(summary, { text: summary.textContent, status: summary.dataset.status });
      }
    }

    setText(element, value) {
      if (element.textContent !== value) element.textContent = value;
    }

    setStatus(element, value) {
      if (element.dataset.status !== value) element.dataset.status = value || '';
    }

    restore(fragment = this.host.querySelector('[data-cc-fragment]')) {
      if (!fragment) return;
      fragment.querySelectorAll('.sh-cc-store').forEach((row) => {
        const original = this.base.get(row);
        if (!original) return;
        const status = row.querySelector('.sh-cc-status');
        const note = row.querySelector('.sh-cc-collect');
        this.setText(status, original.statusText);
        this.setStatus(status, original.status);
        this.setText(note, original.noteText);
        this.setStatus(note, original.noteStatus);
        if (note.hidden !== original.noteHidden) note.hidden = original.noteHidden;
      });
      const summary = fragment.querySelector('[data-cc-summary]');
      const original = summary && this.summaryBase.get(summary);
      if (original) {
        this.setText(summary, original.text);
        this.setStatus(summary, original.status);
      }
    }

    sync() {
      if (this.destroyed) return;
      const form = this.bindForm();
      const signal = readSignal(form, this.isVisible);
      if (this.guard && (!this.guard.previous.button.isConnected ||
          !this.guard.previous.campaign.isConnected || !String(this.guard.previous.campaign.value || '').trim())) {
        this.guard.sawInactive = true;
      }
      const fragment = this.host.querySelector('[data-cc-fragment]');
      if (!fragment || cleanId(fragment.dataset.variantId) !== this.variantId) return;
      this.remember(fragment);
      let active = false;
      let uncertain = false;
      if (this.override) active = this.override.enabled;
      else if (signal && signal.variantId === this.variantId &&
          (!signal.productId || signal.productId === cleanId(this.host.dataset.productId))) {
        const current = !this.guard || this.guard.sawInactive || signalChanged(this.guard.previous, signal);
        active = signal.eligible && current;
        uncertain = !signal.eligible || !current;
        if (current) this.guard = null;
      } else if (this.guard && !this.guard.sawInactive) uncertain = true;

      // Shopify's native pickup flag is not a physical stock count. Essential
      // therefore overrides native "available" too; it must not retain a
      // "Usually ready" promise on an active preorder.
      const fields = controls(form);
      const hasSellingPlan = fields.some((field) => field.name === 'selling_plan' &&
        !field.disabled && String(field.value || '').trim());
      const requiresContact = this.override?.requiresContact || signal?.requiresContact || hasSellingPlan;
      const defaultNote = requiresContact
        ? fragment.dataset.preorderContactNote || 'Contact store about collection when stock arrives.'
        : fragment.dataset.preorderNote || 'Pre-order for Click & Collect. Collect when stock arrives.';
      // A detected selling plan always uses neutral collection wording, even
      // if an external event supplies a more optimistic message.
      const message = requiresContact ? defaultNote : this.override?.message || defaultNote;
      let patched = 0;
      let pending = 0;
      let ready = 0;
      fragment.querySelectorAll('.sh-cc-store').forEach((row) => {
        const original = this.base.get(row);
        if (!original) return;
        const status = row.querySelector('.sh-cc-status');
        const note = row.querySelector('.sh-cc-collect');
        const eligibleRow = row.dataset.preorderAllowed === 'true' && row.dataset.pickupReady === 'true';
        const applies = active && eligibleRow;
        const waiting = uncertain && eligibleRow;
        const text = applies ? fragment.dataset.preorderLabel || 'Pre-order'
          : waiting ? fragment.dataset.unknownLabel || 'Check availability' : original.statusText;
        this.setText(status, text);
        this.setStatus(status, applies ? 'preorder' : waiting ? 'unknown' : original.status);
        this.setText(note, applies ? message : waiting ? '' : original.noteText);
        this.setStatus(note, applies ? 'preorder' : waiting ? 'unknown' : original.noteStatus);
        const hidden = applies ? false : waiting ? true : original.noteHidden;
        if (note.hidden !== hidden) note.hidden = hidden;
        if (applies) patched += 1;
        else if (waiting) pending += 1;
        else if (row.dataset.pickupReady === 'true') ready += 1;
      });
      const summary = fragment.querySelector('[data-cc-summary]');
      const original = summary && this.summaryBase.get(summary);
      if (original) {
        let text = original.text;
        let state = original.status;
        if (patched || pending) {
          if (ready > 0) {
            text = ready === 1 ? fragment.dataset.summaryOne || 'Available at 1 store'
              : (fragment.dataset.summaryMany || 'Available at [count] stores').replace('[count]', String(ready));
            state = 'stock';
          } else if (patched > 0) {
            text = requiresContact
              ? fragment.dataset.preorderContactSummary || 'Contact store for preorder collection'
              : fragment.dataset.preorderSummary || 'Pre-order for Click & Collect';
            state = 'preorder';
          } else {
            text = fragment.dataset.unknownSummary || 'Contact a store to confirm availability';
            state = 'unknown';
          }
        }
        this.setText(summary, text);
        this.setStatus(summary, state);
      }
    }

    destroy() {
      this.restore();
      this.destroyed = true;
      this.observer?.disconnect();
      this.host.removeEventListener('sh:cc:loading', this.loading);
      this.host.removeEventListener('sh:cc:updated', this.updated);
      this.document.removeEventListener('sh:preorder-change', this.explicit);
    }
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { EssentialAdapter, readSignal, signalChanged };
    return;
  }
  if (!global.document || global.__shClickCollectEssential) return;
  global.__shClickCollectEssential = true;
  const adapters = new Map();
  const scan = () => {
    adapters.forEach((adapter, host) => {
      if (!host.isConnected || host.dataset.essentialEnabled !== 'true') {
        adapter.destroy();
        adapters.delete(host);
      }
    });
    global.document.querySelectorAll('sh-click-collect[data-essential-enabled="true"]').forEach((host) => {
      if (!adapters.has(host)) adapters.set(host, new EssentialAdapter(host));
    });
  };
  const observer = new MutationObserver(scan);
  observer.observe(global.document.documentElement, { childList: true, subtree: true });
  global.document.addEventListener('shopify:section:load', scan);
  global.document.addEventListener('DOMContentLoaded', scan, { once: true });
  scan();
})(typeof window === 'object' ? window : globalThis);
